import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { FormService } from 'src/app/shared/services/form.service';
import { Chart, ChartData, ChartConfiguration, ChartDataset, ChartOptions } from 'chart.js';
import { SharedService } from 'src/app/features/service/shared.service';
import { maintainRatio } from 'src/app/shared/util/graph.utils';
import DragDataPlugin from 'chartjs-plugin-dragdata';
import { BaseChartDirective } from 'ng2-charts';
import { PrecastFigureOrderBucket } from 'src/app/shared/models/FigureOrderItem';
import { ReportService } from '../../../services/precast-report.service';

Chart.register(DragDataPlugin);

const EMPTY_SCALE = (): FigureScale => ({
  maxX: null,
  maxY: null,
});

interface FigureScale {
  maxX: number | null;
  maxY: number | null;
}

interface CurvePoint {
  moistureContent: number;
  tr: number;
}

interface FigureObject {
  maxX: number | null;
  maxY: number | null;
  legendHeader: string | null;
  figureNumber: number;
  requirements: string[];
}

interface PrecastFigureSampleCurve {
  curve: CurvePoint[];
  curveLabel : string;
  average: boolean;
  worksheetID: string;
}

interface ChartCurvePoint {
  x: number;
  y: number;
  curveIndex: number;
  sampleIndex: number;
  average: boolean;
}

export interface PrecastFigureSample {
  sampleLabel: string;
  averageCurve : CurvePoint[];
  hideAll: boolean;
  hideActual: boolean;
  hideAverage : boolean;
  curves : PrecastFigureSampleCurve[];
  precastSampleID : string;
  historicalCurvePoints : CurvePoint[][];
}

export interface PrecastFigureObject extends FigureObject {
  precastFigureSamples : PrecastFigureSample[]
}

@Component({
    selector: 'app-thermal-resistivity-precast',
    templateUrl: './thermal-resistivity-precast.component.html',
    styleUrls: ['./thermal-resistivity-precast.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ThermalResistivityPrecastComponent implements OnInit {
  private static readonly MAX_POINTS_PER_FIGURE = 10;

  private static readonly POINTS_TO_PX = 96 / 72;

  readonly chartWidthPt = 424.636456692913;

  readonly chartHeightPt = 496.816771652543;

  readonly chartWidthPx = this.chartWidthPt * ThermalResistivityPrecastComponent.POINTS_TO_PX * 1.2;

  readonly chartHeightPx = this.chartHeightPt * ThermalResistivityPrecastComponent.POINTS_TO_PX * 1.2;

  private static readonly CHART_BORDER_PX = 1;

  readonly chartBoxWidthPx = this.chartWidthPx + (ThermalResistivityPrecastComponent.CHART_BORDER_PX * 2);

  private static readonly LINE_COLORS = ['#FF0000', // Red
    '#00B050',
    '#00B0F0',
    '#7030A0',
    '#BF8F00',
    '#000000',
  ];

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  projectId = '';
  soilType = '';
  projectName = '';
  companyName = '';
  projectLocation = '';
  isMetricValue = false;

  form!: FormGroup;
  drawerActive = false;
  prevDisabled = true;
  nextDisabled = false;

  pageIndex = -1;
  numberOfFigures = 0;

  chartData: ChartData<'line'> = {
    datasets: [{
      data: [],
    }],
  };

  chartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        type: 'linear',
      },
    },
  };

  figureOrderingPopupOpen = false;
  figureNavOpen = true;

  figures: PrecastFigureObject[] = [];
  currentFigure: PrecastFigureObject;
  visible : PrecastFigureSampleCurve[] = [];

  constructor(
    public activatedRoute: ActivatedRoute,
    private reportService: ReportService,
    private formService: FormService,
    private sharedService: SharedService,
  ) {}

  ngOnInit(): void {
    this.soilType = 'precast';
    this.projectId = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    this.getProjectInfo(this.projectId, this.soilType);
    this.sharedService.soilTypes(this.soilType);
  }

  getProjectInfo(projectId: string, soilType: string): void {
    this.formService?.fetchFormData(projectId, soilType)?.subscribe({
      next: (data) => {
        this.projectName = data.project.projectName;
        this.companyName = data.company.name;
        this.projectLocation = data.project.projectLocation ?? '';
        this.getReportInfo();
      },
      error: () => {},
    });
  }

  getReportInfo(): void {
    this.reportService.fetchReportData(this.projectId).subscribe({
      next: (data) => {
        this.isMetricValue = data.isMetricUnit;
        this.getGraphData();
      },
    });
  }

  getGraphData(): void {
    this.reportService.getGraphData(this.projectId)?.subscribe({
      next: (graphData: PrecastFigureObject[]) => {
        this.pageIndex = -1;
        this.figures = graphData;
        this.numberOfFigures = this.figures.length;
        this.figures.forEach((figure) => {
          figure.precastFigureSamples.forEach((precastFigureSample) => {
            precastFigureSample.hideAll = false;
            precastFigureSample.hideActual = false;
            precastFigureSample.hideAverage = false;
            precastFigureSample.averageCurve = [];
            precastFigureSample.curves = precastFigureSample.curves ?? [];
            precastFigureSample.historicalCurvePoints = precastFigureSample.historicalCurvePoints ?? [];
          });
        });
        this.setPage(0);
      },
      error: () => {},
    });
  }

  getCurveColor(curveIndex: number): string {
    const colors = ThermalResistivityPrecastComponent.LINE_COLORS;
    return colors[curveIndex % colors.length];
  }

  toggleCurveAverageSelection(sampleIndex: number, curveIndex : number): void {
    const average = !this.currentFigure.precastFigureSamples[sampleIndex].curves[curveIndex].average;
    this.currentFigure.precastFigureSamples[sampleIndex].curves[curveIndex].average = average;
    this.recomputeAveragedGraphs();
    this.setChartData();
  }

  openHistoricalCurvePoints(sampleIndex: number): void {
    const sample = this.currentFigure.precastFigureSamples[sampleIndex];
    if (!sample) return;
    const average = ThermalResistivityPrecastComponent.toPointTuples(sample.averageCurve ?? []);
    const historical = (sample.historicalCurvePoints ?? []).map(ThermalResistivityPrecastComponent.toPointTuples);
    const params = [
      `label=${encodeURIComponent(sample.sampleLabel ?? '')}`,
      `metric=${this.isMetricValue}`,
      `average=${encodeURIComponent(JSON.stringify(average))}`,
      `historical=${encodeURIComponent(JSON.stringify(historical))}`,
    ].join('&');
    window.open(`/report/historical/precast?${params}`, '_blank');
  }

  private static toPointTuples(points: CurvePoint[]): number[][] {
    return (points ?? []).map((point) => [point.moistureContent, point.tr]);
  }

  toggleSampleVisibility(sampleIndex: number): void {
    const all = !this.currentFigure.precastFigureSamples[sampleIndex].hideAll;
    this.currentFigure.precastFigureSamples[sampleIndex].hideAll = all;
    this.setChartData();
  }

  toggleActualCurves(sampleIndex: number): void {
    const actual = !this.currentFigure.precastFigureSamples[sampleIndex].hideActual;
    this.currentFigure.precastFigureSamples[sampleIndex].hideActual = actual;
    this.setChartData();
  }

  toggleAverageCurve(sampleIndex: number): void {
    const average = !this.currentFigure.precastFigureSamples[sampleIndex].hideAverage;
    this.currentFigure.precastFigureSamples[sampleIndex].hideAverage = average;
    this.setChartData();
  }

  canAddPoint(sampleIndex: number, curveIndex : number): boolean {
    const { curve } = this.currentFigure.precastFigureSamples[sampleIndex].curves[curveIndex];
    return !!curve && curve.length < ThermalResistivityPrecastComponent.MAX_POINTS_PER_FIGURE;
  }

  addPointBetween(sampleIndex: number, curveIndex : number, prevIndex: number): void {
    const { curve } = this.currentFigure.precastFigureSamples[sampleIndex].curves[curveIndex];
    if (!curve || curve.length >= ThermalResistivityPrecastComponent.MAX_POINTS_PER_FIGURE) return;
    const prev = curve[prevIndex];
    const next = curve[prevIndex + 1];
    if (!prev || !next) return;
    curve.splice(prevIndex + 1, 0, {
      moistureContent: (prev.moistureContent + next.moistureContent) / 2,
      tr: (prev.tr + next.tr) / 2,
    });
    this.setChartData();
  }

  private static isChartCurvePoint(value: unknown): value is ChartCurvePoint {
    const point = value as ChartCurvePoint | null;
    return !!point
      && typeof point.x === 'number'
      && typeof point.y === 'number'
      && typeof point.sampleIndex === 'number'
      && typeof point.curveIndex === 'number';
  }

  private getCurvePoint(value: ChartCurvePoint, pointIndex: number): CurvePoint | undefined {
    return this.currentFigure?.precastFigureSamples[value.sampleIndex]
      ?.curves[value.curveIndex]?.curve[pointIndex];
  }

  private isDraggablePoint(value: unknown, pointIndex: number): boolean {
    if (!ThermalResistivityPrecastComponent.isChartCurvePoint(value)) return false;
    if (value.average) return false;
    return !!this.getCurvePoint(value, pointIndex);
  }

  private isEndpoint(value: ChartCurvePoint, pointIndex: number): boolean {
    const curve = this.currentFigure?.precastFigureSamples[value.sampleIndex]
      ?.curves[value.curveIndex]?.curve;
    if (!curve) return false;
    return pointIndex === 0 || pointIndex === curve.length - 1;
  }

  isLastPoint(sampleIndex: number, curveIndex : number, pointIndex: number): boolean {
    const { curve } = this.currentFigure.precastFigureSamples[sampleIndex].curves[curveIndex];
    return !!curve && pointIndex === curve.length - 1;
  }

  updatePoint(sampleIndex: number, curveIndex : number, pointIndex: number, axis: 'x' | 'y', event: Event): void {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) return;
    const { curve } = this.currentFigure.precastFigureSamples[sampleIndex].curves[curveIndex];
    const point = curve?.[pointIndex];
    if (!point) return;
    if (axis === 'x' && this.isLastPoint(sampleIndex, curveIndex, pointIndex)) return;
    if (axis === 'x') {
      point.moistureContent = value;
    }
    if (axis === 'y') {
      point.tr = value;
    }
    this.recomputeAveragedGraphs();
    this.setChartData();
  }

  deletePoint(sampleIndex: number, curveIndex : number, pointIndex: number): void {
    const { curve } = this.currentFigure.precastFigureSamples[sampleIndex].curves[curveIndex];
    if (!curve || curve.length <= 1) return;
    curve.splice(pointIndex, 1);
    this.setChartData();
  }

  goToFigure(index: number): void {
    this.setPage(index);
  }

  setChartData(): void {
    const editableDataset : ChartDataset<'line'>[] = [];
    const averageDataset : ChartDataset<'line'>[] = [];
    this.currentFigure.precastFigureSamples.forEach((precastFigureSample, sampleIndex) => {
      const color = this.getCurveColor(sampleIndex);
      precastFigureSample.curves.forEach((curve, curveIndex) => {
        editableDataset.push({
          borderColor: color,
          backgroundColor: color,
          pointBackgroundColor: color,
          tension: 0.4,
          pointRadius: 5,
          data: curve.curve.map((point) => ({
            x: point.moistureContent,
            y: point.tr,
            curveIndex,
            sampleIndex,
            average: false,
          })),
          borderDash: [5, 5],
          hidden: precastFigureSample.hideActual || precastFigureSample.hideAll,
        });
      });
      averageDataset.push({
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: color,
        tension: 0.4,
        pointRadius: 5,
        data: precastFigureSample.averageCurve.map((point) => ({
          x: point.moistureContent,
          y: point.tr,
          curveIndex: -1,
          sampleIndex,
          average: true,
        })),
        hidden: precastFigureSample.hideAverage || precastFigureSample.hideAll,
      });
    });

    const datasets: ChartDataset<'line'>[] = [
      ...editableDataset,
      ...averageDataset,
    ];

    this.chartData = {
      ...this.chartData,
      datasets,
    };
    this.chartOptions = this.setChartOptions();
    this.chart?.update();
  }

  private recomputeAveragedGraphs(): void {
    this.currentFigure.precastFigureSamples.forEach((precastFigureSample) => {
      const averagedCurves : CurvePoint[][] = [];
      precastFigureSample.curves.forEach((curve) => {
        if (curve.average) {
          averagedCurves.push(curve.curve);
        }
      });
      const averaged : CurvePoint[] = [];
      if (averagedCurves.length === 0) {
        precastFigureSample.averageCurve = averaged;
        return;
      }
      const minLength = Math.min(...averagedCurves.map((curve) => curve.length));
      for (let i = 0; i < minLength; i += 1) {
        let totalX = 0;
        let totalY = 0;
        averagedCurves.forEach((curve) => {
          totalX += curve[i].moistureContent;
          totalY += curve[i].tr;
        });
        averaged.push({
          moistureContent: totalX / averagedCurves.length,
          tr: totalY / averagedCurves.length,
        });
      }
      precastFigureSample.averageCurve = averaged;
    });
  }

  roundUpBy5 = (num: number): number => Math.ceil(num / 5) * 5;
  roundUpBy50 = (num: number): number => Math.ceil(num / 50) * 50;
  roundUpByPoint5 = (num: number): number => Math.ceil(num / 0.5) * 0.5;

  onMaxXChange(event: Event): void {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) {
      this.applyMaxScale(EMPTY_SCALE());
      return;
    }
    const ratio = this.isMetricValue ? 0.1 : 10;
    const scaleX = Math.max(15, this.roundUpBy5(value));
    this.applyMaxScale({
      maxX: scaleX,
      maxY: ratio * scaleX,
    });
  }

  onMaxYChange(event: Event): void {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isNaN(value)) {
      this.applyMaxScale(EMPTY_SCALE());
      return;
    }
    const ratio = this.isMetricValue ? 0.1 : 10;
    const nextY = this.isMetricValue ? this.roundUpByPoint5(value) : this.roundUpBy50(value);
    const scaleY = Math.max(this.isMetricValue ? 1.5 : 50, nextY);
    this.applyMaxScale({
      maxX: scaleY / ratio,
      maxY: scaleY,
    });
  }

  applyScaleToAllFigures(): void {
    const { maxX, maxY } = this.currentFigure;
    this.reportService
      .updateAllFiguresScales(this.projectId, maxX ?? null, maxY ?? null)
      .subscribe({
        next: () => {
          this.figures.forEach((figure) => {
            figure.maxX = maxX;
            figure.maxY = maxY;
          });
          this.setChartData();
        },
      });
  }

  private applyMaxScale(scale: FigureScale): void {
    this.currentFigure.maxX = scale.maxX;
    this.currentFigure.maxY = scale.maxY;
    this.setChartData();
  }

  private getGraphScale(): { y: number; x: number } {
    const scale = {
      maxX: this.currentFigure.maxX, maxY: this.currentFigure.maxY,
    };
    if (scale?.maxX != null && scale?.maxY != null) {
      return {
        x: scale.maxX,
        y: scale.maxY,
      };
    }
    const { precastFigureSamples } = this.currentFigure;
    const allPoints = precastFigureSamples
      .flatMap((precastFigureSample) => precastFigureSample.curves.flatMap((curve) => curve.curve.flatMap((curvePoint) => curvePoint)));
    const maxMc = Math.max(0, ...allPoints.map(((p) => p.moistureContent)));
    const maxTr = Math.max(0, ...allPoints.map(((p) => p.tr)));
    return maintainRatio(maxMc, maxTr, this.isMetricValue);
  }

  setChartOptions(): ChartOptions<'line'> {
    const scale = this.getGraphScale();
    const trText = this.isMetricValue ? 'THERMAL RESISTIVITY (K.m/W)' : 'THERMAL RESISTIVITY (°C -cm/W)';
    const yGridStep = this.isMetricValue ? 0.1 : 10;
    const yLabelStep = this.isMetricValue ? 0.5 : 50;
    return {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        dragData: {
          dragX: true,
          dragY: true,
          showTooltip: true,
          onDragStart: (_e: unknown, _datasetIndex: number, pointIndex: number, value: unknown) => this.isDraggablePoint(value, pointIndex),
          onDrag: (_e: unknown, _datasetIndex: number, pointIndex: number, value: unknown) => {
            if (!this.isDraggablePoint(value, pointIndex)) return false;
            const dragged = value as ChartCurvePoint;
            if (this.isEndpoint(dragged, pointIndex)) {
              dragged.x = this.getCurvePoint(dragged, pointIndex)?.moistureContent ?? dragged.x;
            }
            return true;
          },
          onDragEnd: (_e: unknown, _datasetIndex: number, pointIndex: number, value: unknown) => {
            if (!this.isDraggablePoint(value, pointIndex)) return;
            const dragged = value as ChartCurvePoint;
            const point = this.getCurvePoint(dragged, pointIndex);
            if (!point) return;
            if (!this.isEndpoint(dragged, pointIndex)) {
              point.moistureContent = dragged.x;
            }
            point.tr = dragged.y;
            this.recomputeAveragedGraphs();
            this.setChartData();
          },
        },
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          displayColors: false,
          callbacks: {
            title: () => '',
            label: (item) => `MC: ${item.parsed.x} %`,
            afterLabel: (item) => `TR: ${item.parsed.y}`,
          },
        },
        title: {
          display: true,
          text: 'THERMAL DRYOUT CURVE',
          color: 'black',
        },
      },
      scales: {
        y: {
          min: 0,
          max: scale.y,
          title: {
            display: true,
            text: trText,
            color: 'black',
            font: {
              weight: 'bold', size: 16,
            },
          },
          ticks: {
            stepSize: yGridStep,
            autoSkip: false,
            callback: (value, indexTick) => {
              if (value === 0 && indexTick === 0) return '';
              const ratio = Number(value) / yLabelStep;
              return Math.abs(ratio - Math.round(ratio)) < 1e-9 ? value : '';
            },
            major: {
              enabled: true,
            },
            color: 'black',
            maxRotation: 0,
            minRotation: 0,
          },
        },
        x: {
          type: 'linear',
          min: 0,
          max: scale.x,
          title: {
            display: true,
            text: 'MOISTURE CONTENT (% DRY WEIGHT)',
            color: 'black',
            font: {
              weight: 'bold', style: 'normal', size: 16,
            },
          },
          ticks: {
            stepSize: 1,
            autoSkip: false,
            includeBounds: true,
            precision: 0,
            callback: (value, indexTick) => {
              if (value === 0 && indexTick === 0) return '0    ';
              return value === scale.x || indexTick % 5 === 0 ? value : '';
            },
            major: {
              enabled: true,
            },
            color: 'black',
            maxRotation: 0,
            minRotation: 0,
          },
          grid: {
            drawTicks: true,
            drawOnChartArea: true,
            tickBorderDash: [0],
          },
        },
      },
    };
  }

  next(): void {
    this.setPage(this.pageIndex + 1);
  }

  prev(): void {
    this.setPage(this.pageIndex - 1);
  }

  private setPage(index: number): void {
    if (index < 0 || index >= this.numberOfFigures || index === this.pageIndex) return;
    this.pageIndex = index;
    this.currentFigure = this.figures[this.pageIndex];
    this.recomputeAveragedGraphs();
    this.setChartData();
    this.navigationButtonCheck();
  }

  navigationButtonCheck(): void {
    this.prevDisabled = this.pageIndex === 0;
    this.nextDisabled = this.pageIndex === this.numberOfFigures - 1;
  }

  toggleFigureNav(): void {
    this.figureNavOpen = !this.figureNavOpen;
    setTimeout(() => this.chart?.chart?.resize());
  }

  onSubmit(): void {
    const figureObject = {
      precastFigureSamples: (this.currentFigure?.precastFigureSamples ?? []).map((sample) => ({
        precastSampleID: sample.precastSampleID,
        curves: sample.curves.map((curve) => ({
          precastWorksheetID: curve.worksheetID,
          average: curve.average,
          curve: curve.curve.map((point) => ({
            moistureContent: point.moistureContent,
            tr: point.tr,
          })),
        })),
      })),
    };
    this.reportService.updatePrecastWorksheets(this.projectId, this.isMetricValue, figureObject).subscribe({
      next: (curvePointsList: CurvePoint[][]) => {
        const flatCurves = (this.currentFigure?.precastFigureSamples ?? []).flatMap((sample) => sample.curves);
        curvePointsList.forEach((curvePoints, index) => {
          if (flatCurves[index]) {
            flatCurves[index].curve = curvePoints;
          }
        });
        this.recomputeAveragedGraphs();
        this.setChartData();
      },
    });
    this.reportService
      .updateFigureProperty(this.projectId, this.currentFigure.figureNumber, this.currentFigure.maxX ?? null, this.currentFigure.maxY ?? null)
      .subscribe();
  }

  previewGraphs(): void {
    this.reportService.createPreCastGraphs(this.isMetricValue, this.projectId, this.currentFigure.figureNumber).subscribe({
      next: (pdfBlob: Blob) => {
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      },
    });
  }

  openFigureOrderingPopup(): void {
    this.figureOrderingPopupOpen = true;
  }

  closeFigureOrderingPopup(): void {
    this.figureOrderingPopupOpen = false;
  }

  saveFigureOrdering(event: {
    figures: PrecastFigureObject[];
    reordered: boolean;
  }): void {
    this.figureOrderingPopupOpen = false;
    const { figures, reordered } = event;
    const figureOrderingBucket: { [bucket: number]: PrecastFigureOrderBucket } = {
    };
    this.figures = figures;
    figures.forEach((figure) => {
      figureOrderingBucket[figure.figureNumber] = {
        legendHeader: figure.legendHeader ?? '',
        figures: figure.precastFigureSamples.map((sample, idx) => ({
          preCastSampleID: sample.precastSampleID,
          figurePosition: idx,
        })),
      };
    });
    this.reportService.orderPrecastFigures(figureOrderingBucket, this.projectId, reordered).subscribe({
      next: () => {
        if (reordered) {
          this.getGraphData();
        }
      },
    });
  }

  openNav(): void {
    this.setDrawerWidth('250px', true);
  }

  closeNav(): void {
    this.setDrawerWidth('0', false);
  }

  private setDrawerWidth(width: string, active: boolean): void {
    const sidenav = document.getElementById('drawer');
    if (sidenav) {
      sidenav.style.width = width;
      this.drawerActive = active;
    }
  }
}
